/**
 * Shared restaurant-creation defaults.
 *
 * Single source of truth for the values both `apps/admin` (RestaurantModal —
 * the "create a 2nd location for an existing tenant" form) and
 * `apps/onboarding` (the "first restaurant for a new tenant" wizard) seed
 * into a fresh restaurant. Centralising these in `@order-up/common` keeps
 * client-onboarding's defaults aligned with what the admin form would have
 * produced for the same merchant — no drift between paths.
 *
 * The schema sources of truth are:
 *   - Restaurant model:    `packages/models/src/locations/Location.ts`
 *   - Zod validator:       `packages/schemas/src/auth/Location.ts`
 *
 * Anything optional in the Zod validator is omitted from these defaults so
 * the merchant only carries data they actually need.
 */

/** ISO 8601 week-day keys. Order matches RestaurantSchema operatingHours record. */
export const DAYS_OF_WEEK = [
  { key: 'monday',    label: 'Monday' },
  { key: 'tuesday',   label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday',  label: 'Thursday' },
  { key: 'friday',    label: 'Friday' },
  { key: 'saturday',  label: 'Saturday' },
  { key: 'sunday',    label: 'Sunday' },
] as const;

export type DayOfWeek = typeof DAYS_OF_WEEK[number]['key'];

/**
 * Sensible 7-day operating-hours seed used when an operator hasn't entered
 * specific hours yet. The shape matches `DayHoursSchema`:
 * `{ open: string, close: string, closed: boolean }` per day.
 *
 * Hours skew slightly later on Fri/Sat (typical restaurant convention).
 * Sunday opens later. None of the days are closed by default — the operator
 * toggles individual days closed via the editor.
 */
export const DEFAULT_OPERATING_HOURS: Record<
  DayOfWeek,
  { open: string; close: string; closed: boolean }
> = {
  monday:    { open: '09:00', close: '22:00', closed: false },
  tuesday:   { open: '09:00', close: '22:00', closed: false },
  wednesday: { open: '09:00', close: '22:00', closed: false },
  thursday:  { open: '09:00', close: '22:00', closed: false },
  friday:    { open: '09:00', close: '23:00', closed: false },
  saturday:  { open: '09:00', close: '23:00', closed: false },
  sunday:    { open: '10:00', close: '21:00', closed: false },
};

/**
 * Default `RestaurantSettings` for newly-provisioned restaurants.
 *
 * Mirrors the tenant-level defaults seeded by `OnboardingApplicationService`:
 *   - `currency: USD` matches tenant.settings.currency.
 *   - `weekStartDay: monday` matches tenant.settings.weekStartDay.
 *   - `dateFormat: MM/DD/YYYY` matches tenant.settings.dateFormat.
 *
 * Operational defaults (industry-standard for a starter restaurant):
 *   - `allowTips: true`         — restaurants are tipped almost universally.
 *   - No acceptance field: an unset `orderAcceptanceMode` resolves to
 *     `manual` (merchant opts in, in the management app).
 *   - `clockFormat: '12h'`      — US convention.
 *
 * Operators can override any of these post-provisioning via the management
 * app's restaurant-settings flow.
 */
export const DEFAULT_LOCATION_SETTINGS = {
  currency: 'USD',
  weekStartDay: 'monday' as const,
  dateFormat: 'MM/DD/YYYY' as const,
  clockFormat: '12h' as const,
  allowTips: true,
};
